<template>
  <div class="maps-component">
    <GMapMap
      :center="mapCenter"
      :zoom="16"
      :options="mapOptions"
      :style="{ width: '100%', height: height + 'px' }"
    >
      <GMapMarker
        v-for="(marker, index) in markers"
        :key="index"
        :position="marker.position"
      />
    </GMapMap>

    <!-- Estados de geocodificación -->
    <div class="geocoding-status mt-3" v-if="showGeocodingStatus">
      <v-alert
        v-if="loadingAddress"
        type="info"
        variant="outlined"
        density="compact"
      >
        <span>Geocodificando dirección...</span>
        <v-progress-linear indeterminate class="mt-2"></v-progress-linear>
      </v-alert>

      <v-alert
        v-else-if="errorAddress != null"
        type="error"
        variant="outlined"
        density="compact"
      >
        {{ errorAddress }}
      </v-alert>

      <v-alert
        v-else-if="scoring > 0 && scoring < 0.75"
        type="error"
        variant="outlined"
        density="compact"
      >
        <div>
          Ubicación geocodificada con confianza muy baja
          ({{ Math.round(scoring * 100) }}%). Las coordenadas no se utilizarán debido a la ambigüedad en la dirección.
        </div>
      </v-alert>

      <v-alert
        v-else-if="lat && lng && scoring >= 0.75 && scoring < 0.85"
        type="warning"
        variant="outlined"
        density="compact"
      >
        <div>
          Ubicación geocodificada con confianza media
          ({{ Math.round(scoring * 100) }}%). Verificar precisión.
        </div>
      </v-alert>

      <v-alert
        v-else-if="lat && lng && errorAddress == null"
        type="success"
        variant="outlined"
        density="compact"
      >
        <div>Dirección geocodificada exitosamente</div>
      </v-alert>
    </div>
  </div>
</template>

<script>
import { geoService } from "@/libs/ws/geoService";

export default {
  name: "Maps",
  props: {
    lat: { default: null },
    lng: { default: null },
    height: { default: 225 },
    // Props para geocodificación
    address: { default: "" },
    location: { default: "" },
    postal_code: { default: "" },
    provincia: { default: null },
    autoGeocode: { default: true },
    disabled: { default: false },
  },
  emits: ["coordinates-updated"],
  data() {
    return {
      mapCenter: { lat: -34.6036844, lng: -58.3815591 },
      mapOptions: {
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: false,
        streetViewControl: true,
        rotateControl: true,
        fullscreenControl: true,
        disableDefaultUi: true,
      },
      markers: [], // Inicializar vacío, se llena solo si hay coordenadas válidas
      // Estados de geocodificación
      loadingAddress: false,
      errorAddress: null,
      addressUsed: null,
      scoring: 0,
    };
  },
  computed: {
    showGeocodingStatus() {
      return (
        this.loadingAddress ||
        this.errorAddress !== null ||
        (this.lat && this.lng) ||
        this.scoring > 0  // Mostrar si hubo un intento de geocodificación
      );
    },
    canGeocode() {
      return this.address && this.location;
    },
    confidenceLevel() {
      if (this.scoring >= 0.9) return "high";
      if (this.scoring >= 0.75) return "medium";
      return "low";
    },
  },
  watch: {
    lat(newLat) {
      this.updateMarkerPosition(newLat, this.lng);
    },
    lng(newLng) {
      this.updateMarkerPosition(this.lat, newLng);
    },
  },
  mounted() {
    this.updateMarkerPosition(this.lat, this.lng);
    if (this.autoGeocode && this.canGeocode) {
      this.geocodeAddress();
    }
  },
  methods: {
    updateMarkerPosition(lat, lng) {
      if (lat !== null && lng !== null && lat !== "" && lng !== "") {
        let finalLat = parseFloat(lat);
        let finalLng = parseFloat(lng);
        
        this.markers = [
          {
            position: {
              lat: finalLat,
              lng: finalLng,
            },
          },
        ];
        this.mapCenter = { lat: finalLat, lng: finalLng };
      } else {
        this.markers = [];
        this.mapCenter = { lat: -34.6036844, lng: -58.3815591 };
      }
    },

    /**
     * Método público para que lo llame el padre cuando un input pierde foco
     */
    handleInputsBlur() {
      if (!this.autoGeocode) return;

      this.errorAddress = null;

      if (this.canGeocode) {
        this.geocodeAddress();
      } else {
        this.emitCoordinatesUpdate(null, null);
      }
    },

    async geocodeAddress() {
      if (!this.canGeocode || this.loadingAddress || this.disabled) return;

      try {
        this.loadingAddress = true;
        this.errorAddress = null;

        const body = {
          address: this.address,
          location: this.location,
          postal_code: this.postal_code,
          provincia: this.provincia != null ? this.provincia.name : null,
        };

        const response = await geoService.geocode(body);

        if (response.status && response.result) {
          this.addressUsed = response.result.address_used;
          this.scoring = response.result.scoring?.queryScore || 0;

          if (this.scoring >= 0.75) {
            this.emitCoordinatesUpdate(response.result.lat, response.result.lng);
            this.updateMarkerPosition(response.result.lat, response.result.lng);
          } else {
            this.emitCoordinatesUpdate(null, null);
            this.updateMarkerPosition(null, null);
            
          }
        } else {
          this.errorAddress =
            response.msg ||
            response.message ||
            "No se pudo geocodificar la dirección";
          
          this.emitCoordinatesUpdate(null, null);
          this.updateMarkerPosition(null, null);
        }
      } catch (error) {
        console.error("Error en geocodificación:", error);
        this.errorAddress =
          error.response?.data?.msg ||
          error.message ||
          "Error al conectar con el servicio de geocodificación";
        
        this.emitCoordinatesUpdate(null, null);
        this.updateMarkerPosition(null, null);
      } finally {
        this.loadingAddress = false;
      }
    },

    emitCoordinatesUpdate(lat, lng) {
      this.$emit("coordinates-updated", { lat, lng });
    },
  },
};
</script>

<style scoped>
.maps-component {
  width: 100%;
}

.geocoding-status .v-alert {
  margin-bottom: 8px;
}

.geocoding-status .v-progress-linear {
  margin-top: 8px;
}
</style>
